import jsPDF from 'jspdf';
import * as QRCodeLib from 'qrcode';
import { encryptAppId, encryptText } from "../../utils/encryption";


/**
 * Load Times New Roman fonts into jsPDF
 * Returns true if fonts loaded successfully, false otherwise
 */
const loadTimesFont = async (doc) => {
  try {
    // Load font files from public/fonts directory
    const fontsToLoad = [
      { file: 'times.ttf', name: 'TimesNewRoman', style: 'normal' },
      { file: 'timesbd.ttf', name: 'TimesNewRoman', style: 'bold' },
      { file: 'timesi.ttf', name: 'TimesNewRoman', style: 'italic' }
    ];

    let successCount = 0;

    for (const font of fontsToLoad) {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/fonts/${font.file}`);
        if (!response.ok) {
          console.warn(`Font file not found: ${font.file}`);
          continue;
        }
        const fontBuffer = await response.arrayBuffer();
        const base64Font = arrayBufferToBase64(fontBuffer);
        doc.addFileToVFS(font.file, base64Font);
        doc.addFont(font.file, font.name, font.style);
        successCount++;
      } catch (err) {
        console.warn(`Failed to load ${font.file}:`, err);
      }
    }

    // Only use Times New Roman if all fonts loaded successfully
    if (successCount === 3) {
      doc.setFont('TimesNewRoman', 'normal');
      console.log('Times New Roman fonts loaded successfully');
      return true;
    } else {
      console.warn('Not all Times New Roman fonts loaded, using helvetica');
      doc.setFont('helvetica', 'normal');
      return false;
    }
  } catch (error) {
    console.error('Error loading Times New Roman fonts:', error);
    doc.setFont('helvetica', 'normal');
    return false;
  }
};
const htmlToText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};
/**
 * Convert ArrayBuffer to Base64
 */
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

/**
 * Load image and convert to base64
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (error) => {
      console.error('Failed to load image:', imagePath, error);
      reject(error);
    };
    img.src = `${process.env.PUBLIC_URL}${imagePath}`;
  });
};

/**
 * Generate Certificate of Accreditation PDF
 * @param {Object} data - Certificate data
 * @param {string} data.labName - Laboratory name
 * @param {string} data.address - Full address
 * @param {string} data.additionaldata - Full address
 * @param {string} data.applicationNo - Full address
 * @param {string} data.certificateNumber - Certificate number
 * @param {string} data.oldCertificateNumber - Certificate number
 * @param {string} data.issueDate - Issue date
 * @param {string} data.validUntil - Valid until date
 * @param {string} data.legalEntity - Name of legal entity
 * @param {string} data.field - Field of accreditation (TESTING/CALIBRATION)
 * @param {string} data.appId - Encrypted application ID
 * @param {string} data.orgId - Encrypted organization ID
 */
export const generateCertificateOfAccreditation = async (data) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Load Times New Roman fonts, returns true if successful
  const fontsLoaded = await loadTimesFont(doc);
  const fontFamily = fontsLoaded ? 'TimesNewRoman' : 'helvetica';

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 3;

  try {
    // Load images from public folder - load each independently
    let headerImage, logoImage, qrImage, directorSignature, ceoSignature;

    // Load header image (try multiple filenames)
    try {
      headerImage = await loadImageAsBase64('/certificate_images/certificatesimageheader.png');
      console.log('✓ Header image loaded (certificatesimageheader.png)');
    } catch (err) {
      try {
        headerImage = await loadImageAsBase64('/certificate_images/certificate_header.png');
        console.log('✓ Header image loaded (certificate_header.png)');
      } catch (err2) {
        console.warn('✗ Header image not found (tried certificatesimageheader.png & certificate_header.png)');
      }
    }

    // Load logo image
    try {
      logoImage = await loadImageAsBase64('/certificate_images/certificate_logo.png');
      console.log('✓ Logo image loaded');
    } catch (err) {
      console.warn('✗ Logo image not found');
    }

    // Load director signature (from data or default)
    try {
      if (data.directorsignature) {
        // Try loading from certificate_images directory if filename is provided
        directorSignature = await loadImageAsBase64(`/certificate_images/${data.directorsignature}`);
        console.log('✓ Director signature loaded from certificate_images');
      } else {
        // Fallback to default signature
        directorSignature = await loadImageAsBase64('/certificate_images/signature.png');
        console.log('✓ Director signature loaded (default)');
      }
    } catch (err) {
      console.warn('✗ Director signature not found');
    }

    // Load CEO signature (from data or default)
    try {
      if (data.ceosignature) {
        // Try loading from uploads directory if filename is provided
        ceoSignature = await loadImageAsBase64(`/uploads/${data.ceosignature}`);
        console.log('✓ CEO signature loaded from uploads');
      } else {
        // Fallback to default signature
        ceoSignature = await loadImageAsBase64('/certificate_images/CEO_signature.png');
        console.log('✓ CEO signature loaded (default)');
      }
    } catch (err) {
      console.warn('✗ CEO signature not found');
    }

    // Generate QR code dynamically
    try {
      const qrCodeUrl = `${process.env.REACT_APP_PAGE_URL}/viewCertificate?appId=${(encryptAppId(data.appId))}&orgId=${(encryptText(data.orgId))}&action=${(encryptText('download'))}`;
      qrImage = await QRCodeLib.toDataURL(qrCodeUrl, {
        width: 300,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      console.log('✓ QR code generated for URL:', qrCodeUrl);
    } catch (err) {
      console.warn('✗ QR code generation failed:', err);
    }

    // Main border (reduced size)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.3);
    doc.roundedRect(8, 6, pageWidth - 16, pageHeight - 12, 1, 1);

    console.log('Logo image status:', logoImage ? 'Loaded ✓' : 'Not loaded ✗');

    // Background watermark logo (NABL logo in center)
    if (logoImage) {
      try {
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.8 }));
        const watermarkSize = 160;
        const watermarkX = (pageWidth - watermarkSize) / 2;
        const watermarkY = (pageHeight - watermarkSize) / 2;
        doc.addImage(logoImage, 'PNG', watermarkX, watermarkY, watermarkSize, watermarkSize);
        doc.restoreGraphicsState();
        console.log('✓✓✓ WATERMARK LOGO ADDED to certificate at center');
      } catch (err) {
        console.error('Failed to add watermark:', err);
      }
    } else {
      console.warn('✗✗✗ WATERMARK NOT ADDED - logo image not available');
      console.warn('Please ensure certificate_logo.png exists in public/certificate_images/');
    }

    let yPos = 14;

    // Add header image
    if (headerImage) {
      const headerWidth = pageWidth - 35;
      const headerHeight = 35;
      const headerX = 25; // Left-aligned with 30mm margin
      doc.addImage(headerImage, 'PNG', headerX, yPos, headerWidth, headerHeight);
      yPos += headerHeight + 8;
    } else {
      yPos += 10;
    }

    // Laboratory Name (XX-Large, Bold)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(21);
    doc.setTextColor(0, 0, 0);
    const labNameLines = doc.splitTextToSize(data.labName || '', pageWidth - 20);
    labNameLines.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    });

    yPos += 3;

    // "has been assessed..." text
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(13);
    doc.text('has been assessed and accredited in accordance with the standard', pageWidth / 2, yPos, { align: 'center' });

    yPos += 12;

    // Standard (ISO) - XX-Large, Bold
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(20);
    doc.text(data.standard || '', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;

    // Standard description in quotes
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(20);
    let standardDesc = '';
    if (data.field === 'Medical Testing') {
      standardDesc = '"Medical laboratories - Requirements for quality and competence"';
    } else {
      standardDesc = '"General Requirements for the Competence of Testing & Calibration Laboratories"';
    }
    const descLines = doc.splitTextToSize(standardDesc, pageWidth - 20);
    descLines.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
    });

    yPos += 5;

    // "for its facilities at" text
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(14);
    doc.text('for its facilities at', pageWidth / 2, yPos, { align: 'center' });

    yPos += 15;

    // Address (Large, Bold)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(13);
    const formatAddress = (address) => {
      if (!address) return '-';

      const parts = address
        .split(',')
        .map(part => part.trim());

      const cleanedParts = [];

      parts.forEach(part => {
        const upperPart = part.toUpperCase();

        // avoid duplicate consecutive values
        if (
          cleanedParts.length === 0 ||
          cleanedParts[cleanedParts.length - 1].toUpperCase() !== upperPart
        ) {
          cleanedParts.push(part);
        }
      });

      return cleanedParts.join(', ');
    };

    const formattedAddress = formatAddress(data.address);

    const addressLines = doc.splitTextToSize(
      formattedAddress,
      pageWidth - 20
    );
    addressLines.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5.5;
    });

    yPos += 5;

    // "in the field of" text
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(14);
    doc.text('in the field of', pageWidth / 2, yPos, { align: 'center' });

    yPos += 14;

    // Field Type (XX-Large, Bold)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(20);
    doc.text((data.field || '-').toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    yPos += 10;

    // Certificate Details Section (matching ASP.NET layout)
    const leftMargin = 12;

    // Certificate Number
    // doc.setFont(fontFamily, 'bold');
    // doc.setFontSize(12);
    // doc.text('Certificate Number:', leftMargin, yPos, { align: 'left' });
    // doc.setFont(fontFamily, 'bold');
    // doc.text(data.certificateNumber || '', leftMargin + 40, yPos, { align: 'left' });(doc.text('In Lieu of') doc.text(data.oldCertificateNumber))

    //By Ayan add old certificate number on 6/62026 10:47 PM
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(12);

    let certificateText = `Certificate Number: ${data.certificateNumber || ''}`;

    if (data.oldCertificateNumber && data.oldCertificateNumber.trim() !== '') {
      certificateText += ` (In Lieu of ${data.oldCertificateNumber})`;
    }

    doc.text(certificateText, leftMargin, yPos, { align: 'left' });

    yPos += 7;

    // Issue Date and Valid Until on same line
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(12);
    doc.text('Issue Date:', leftMargin, yPos, { align: 'left' });
    doc.setFont(fontFamily, 'bold');
    doc.text(data.issueDate || '', leftMargin + 40, yPos, { align: 'left' });

    // Valid Until on same row (right side)
    doc.setFont(fontFamily, 'bold');
    doc.text('Valid Until:', leftMargin + 110, yPos, { align: 'left' });
    doc.setFont(fontFamily, 'bold');
    doc.text(data.validUntil || '', leftMargin + 135, yPos, { align: 'left' });

    doc.setTextColor(0, 0, 0);

    yPos += 10;

    if (data.additionaldata) {
      const additionalText = htmlToText(data.additionaldata);

      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(11);

      const additionalLines = doc.splitTextToSize(additionalText, pageWidth - 20);

      additionalLines.forEach(line => {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      });

      yPos += 3;
    }

    // Disclaimer text (medium font, Times New Roman, justified/center)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11.3);
    doc.setTextColor(0, 0, 0);

    const disclaimerLine1 = 'This certificate remains valid for the Scope of Accreditation as specified in the annexure subject to continued ';
    const disclaimerLine2 = 'satisfactory compliance to the above standard & the relevant requirements of NABL.';

    const line1Split = doc.splitTextToSize(disclaimerLine1, pageWidth - 2);
    line1Split.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
    });

    const line2Split = doc.splitTextToSize(disclaimerLine2, pageWidth - 2);
    line2Split.forEach(line => {
      doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
    });

    yPos += 1;

    // Website reference (small font)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11);
    const websiteText = '(To see the scope of accreditation of this laboratory, you may also visit NABL website www.nabl-india.org)';
    doc.text(websiteText, pageWidth / 2, yPos, { align: 'center', maxWidth: pageWidth - 20 });

    yPos += 10;

    // Name of Legal Entity
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(11);
    const label = "NAME OF LEGAL ENTITY: ";
    const value = data.legalEntity || "";

    // width of label
    const labelWidth = doc.getTextWidth(label);

    // split only the value
    const splitValue = doc.splitTextToSize(value, pageWidth - leftMargin - labelWidth - 15);

    // print first line
    doc.text(label + splitValue[0], leftMargin, yPos);

    // print remaining lines aligned with value start
    for (let i = 1; i < splitValue.length; i++) {
      yPos += 6;
      doc.text(splitValue[i], leftMargin + labelWidth, yPos);
    }
    yPos += 16;

    // "Signed for and on behalf of NABL" (x-large)
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Signed for and on behalf of NABL', pageWidth / 2, yPos, { align: 'center' });

    yPos += 15;

    // Signature section: QR Code, Director, CEO (matching table layout)
    // Only show if director information is provided
    const showSignatures = data.certificateNumber && data.validUntil && data.stage === '7d';

    if (showSignatures) {
      const qrX = 15;
      const dirX = 95;
      const ceoX = 172;

      // QR Code (left) - Dynamically generated
      if (qrImage) {
        const qrSize = 28;
        doc.addImage(qrImage, 'PNG', qrX, yPos, qrSize, qrSize);
        console.log('✓ QR code added to certificate');
      } else {
        const qrSize = 30;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.rect(qrX, yPos, qrSize, qrSize);
        doc.setFontSize(7);
        doc.text('QR', qrX + qrSize / 2, yPos + qrSize / 2, { align: 'center' });
        console.warn('✗ QR code not added - generation failed');
      }

      // Director Signature (center)
      if (directorSignature) {
        doc.addImage(directorSignature, 'PNG', dirX - 15, yPos, 35, 15);
      }

      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(11);
      doc.text(data.directorname || 'Director', dirX, yPos + 20, { align: 'center' });
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(11);
      doc.text('Director', dirX, yPos + 26, { align: 'center' });

      // CEO Signature (right)
      if (ceoSignature) {
        doc.addImage(ceoSignature, 'PNG', ceoX - 20, yPos, 40, 18);
      }

      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(11);
      doc.text('Dr. Ramanand N. Shukla', ceoX, yPos + 20, { align: 'center' });
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(11);
      doc.text('Chief Executive Officer', ceoX, yPos + 26, { align: 'center' });
    }
    const now = new Date();

    const timestamp =
      now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");

    // Save the PDFd
    const fileName = `Certificate-${data.certificateNumber || data.applicationNo}-${timestamp}.pdf`;
    // Return blob for upload and filename
    const pdfBlob = doc.output('blob');
    console.log('✓ Scope PDF generated successfully');

    return { pdfBlob, fileName, doc };

  } catch (error) {
    console.error('Error generating certificate:', error);
    throw new Error(`Failed to generate certificate: ${error.message || 'Unknown error'}`);
  }
};

export default {
  generateCertificateOfAccreditation
};
